import type { TransferDir, TransferVenue } from '~/utils/transfer/types'
import {
  DEFAULT_MINOR_SCALE,
  displayToMinorUnits,
  inferMinorScale,
  moneyPercent,
  parseMoney,
} from '~/utils/transfer/money'
import { useVenueCatalog } from './useVenueCatalog'
import { transferVenue, updateVenueSettings } from '~/api/wallet'
import { ApiError } from '~/types/api'
import { createUuid } from '~/utils/uuid'

/** 额度转换表单：方向 / 场馆 / 金额 / 自动开关 / 校验提交 */
export function useTransferForm() {
  const { t } = useI18n()
  const { info, success } = useNotify()
  const userStore = useUserStore()
  const catalog = useVenueCatalog()
  const { refreshWallet } = useWallet()

  const dir = ref<TransferDir>('in')
  const venueKey = ref('')
  const venue = ref<TransferVenue | null>(null)
  const amount = ref('')
  const auto = ref(true)
  const busy = ref(false)
  const settingsBusy = ref(false)
  const err = ref('')
  /** 网络超时后重试须沿用同一幂等单号 */
  let clientOrderId = ''

  watch(
    [venueKey, catalog.venues],
    () => {
      const found = catalog.venueOf(venueKey.value)
      if (found) {
        venue.value = found
        return
      }
      if (venueKey.value && venue.value?.k === venueKey.value) return
      const next = catalog.venues.value.find(v => !v.mnt && !v.pend) ?? catalog.venues.value[0]
      if (!next) return
      venueKey.value = next.k
      venue.value = next
    },
    { immediate: true },
  )

  watch(
    () => catalog.listDto.value?.auto_transfer,
    v => {
      if (typeof v === 'boolean') auto.value = v
    },
    { immediate: true },
  )

  const centralAvail = computed(() =>
    parseMoney(catalog.listDto.value?.central_available_display || userStore.wallet.available),
  )

  const fromAvail = computed(() => {
    if (!venue.value) return 0
    return dir.value === 'in' ? centralAvail.value : catalog.venueBal(venue.value)
  })

  const ctaLabel = computed(() => {
    if (busy.value) return t('me.tfBusy')
    if (catalog.listPending.value && !venue.value) return t('me.tfLoading')
    const name = venue.value?.n ?? ''
    return dir.value === 'in' ? t('me.tfConfirmIn', { name }) : t('me.tfConfirmOut', { name })
  })

  const amountSym = computed(() => (userStore.wallet.ledgerCurrency === 'CNY' ? '¥' : 'USDT'))

  const mainAcctLabel = computed(() =>
    t('me.tfMainAcct', { code: userStore.wallet.ledgerCurrency }),
  )

  const minorScale = computed(() => {
    const dto = catalog.listDto.value
    if (!dto) return DEFAULT_MINOR_SCALE
    return inferMinorScale(dto.min_amount, dto.min_amount_display)
  })

  const resetOrderId = () => {
    clientOrderId = ''
  }

  watch(amount, () => {
    if (err.value) err.value = ''
    resetOrderId()
  })

  const swapDir = () => {
    dir.value = dir.value === 'in' ? 'out' : 'in'
    err.value = ''
    resetOrderId()
  }

  const toggleAuto = async () => {
    if (settingsBusy.value) return
    const next = !auto.value
    auto.value = next
    settingsBusy.value = true
    try {
      const res = await updateVenueSettings({ auto_transfer: next })
      auto.value = res.auto_transfer
      info(res.auto_transfer ? t('me.tfAutoOn') : t('me.tfAutoOff'))
    } catch {
      auto.value = !next
    } finally {
      settingsBusy.value = false
    }
  }

  const minorOf = (v: number | string) => BigInt(displayToMinorUnits(v, minorScale.value))

  const setPct = (pct: number) => {
    amount.value = moneyPercent(fromAvail.value, pct)
  }

  const pickVenue = (k: string): boolean => {
    const v = catalog.venueOf(k)
    if (!v) return false
    if (v.mnt) {
      info(t('me.tfErrMaint', { name: v.n }))
      return false
    }
    if (v.pend) {
      info(t('me.tfErrPend', { name: v.n }))
      return false
    }
    venueKey.value = k
    err.value = ''
    resetOrderId()
    return true
  }

  const validate = (): string => {
    const v = venue.value
    if (!v) return t('me.tfErrAmt')
    if (v.mnt) return t('me.tfErrMaint', { name: v.n })
    if (v.pend) return t('me.tfErrPend', { name: v.n })
    if (dir.value === 'out' && !v.balKnown) return t('me.tfErrUnknown', { name: v.n })
    const entered = minorOf(amount.value)
    if (entered <= 0n) return t('me.tfErrAmt')
    if (dir.value === 'in' && entered < minorOf(catalog.minTransfer.value)) {
      return t('me.tfErrMin', { min: catalog.formatCentral(catalog.minTransfer.value) })
    }
    if (entered > minorOf(fromAvail.value)) {
      const amt = catalog.formatCentral(fromAvail.value)
      return dir.value === 'in'
        ? t('me.tfErrCentral', { amt })
        : t('me.tfErrVenue', { name: v.n, amt })
    }
    return ''
  }

  const submit = async () => {
    const v = venue.value
    if (busy.value || !v) return
    const msg = validate()
    if (msg) {
      err.value = msg
      return
    }

    const enteredText = amount.value
    const allOut = dir.value === 'out' && minorOf(enteredText) >= minorOf(fromAvail.value)
    if (!clientOrderId) clientOrderId = createUuid()

    busy.value = true
    err.value = ''
    try {
      const res = await transferVenue(v.k, dir.value, {
        amount: allOut ? '0' : displayToMinorUnits(enteredText, minorScale.value),
        client_order_id: clientOrderId,
      })
      catalog.patchVenueList(res)
      userStore.setAvailableDisplay(res.central_available_display)
      void refreshWallet().catch(() => {})
      void catalog.refreshVenues()
      const shown = catalog.formatCentral(parseMoney(res.amount_display) || parseMoney(enteredText))
      amount.value = ''
      resetOrderId()
      success(
        dir.value === 'in'
          ? t('me.tfOkIn', { name: v.n, amt: shown })
          : t('me.tfOkOut', { name: v.n, amt: shown }),
      )
    } catch (e) {
      if (e instanceof ApiError && e.status === 504) err.value = t('me.tfErrTimeout')
      else resetOrderId()
    } finally {
      busy.value = false
    }
  }

  return {
    dir,
    venueKey,
    venue,
    amount,
    auto,
    busy,
    settingsBusy,
    err,
    centralAvail,
    ctaLabel,
    amountSym,
    mainAcctLabel,
    minTransfer: catalog.minTransfer,
    formatCentral: catalog.formatCentral,
    formatVenueAmt: catalog.formatVenueAmt,
    catLabel: catalog.catLabel,
    catFilter: catalog.catFilter,
    cats: catalog.cats,
    venues: catalog.venues,
    listFetching: catalog.listFetching,
    listPending: catalog.listPending,
    swapDir,
    toggleAuto,
    setPct,
    pickVenue,
    submit,
  }
}
